/**
 * InventoryTimeline — live SSE event log panel.
 *
 * Shows the full event sequence of the current inventory-resolve
 * stream as a vertical timeline. Entries slide in as they arrive;
 * there is no buffering or delay — each entry corresponds directly
 * to one SSE event that landed over the wire.
 *
 * Behaviour:
 *   - Collapsed by default (shows "N events ▸" toggle).
 *   - Auto-expands when `stream.phase === "classifying"` and there
 *     are ≥ 2 entries (enough to be interesting).
 *   - Auto-collapses 1.5s after `stream.phase === "done"`.
 *   - Each new entry animates in with a 200ms slide + fade.
 *   - Entries are capped at 20 (oldest drop off top) to avoid DOM
 *     bloat on large libraries.
 *   - The active entry (last classifying event) has a breathing
 *     dot; terminal entries are static.
 *
 * Icon key:
 *   ◉  checking_cache / cache_miss / cache_hit
 *   ⬤  classifying / labelled / params_*
 *   ✓  done
 *   ✕  error
 *
 * Zero dependencies beyond the existing token system and
 * useInventoryStream types.
 */

import { makeStyles } from "@fluentui/react-components";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { fonts, inkColors, statusColors, surfaceColors } from "../../tokens";
import type { StreamPhase, TimelineEntry } from "../hooks/useInventoryStream";
import { aiColors } from "../tokens";

// ─── Keyframe injection ─────────────────────────────────────────────

const KF_ID = "inv-timeline-keyframes";
if (typeof document !== "undefined" && !document.getElementById(KF_ID)) {
  const el = document.createElement("style");
  el.id = KF_ID;
  el.textContent = `
    @keyframes itl-slide-in {
      from { opacity: 0; transform: translateY(6px); }
      to   { opacity: 1; transform: translateY(0);   }
    }
  `;
  document.head.appendChild(el);
}

// ─── Entry display helpers ─────────────────────────────────────────

const MAX_ENTRIES = 20;

function entryDotColor(entry: TimelineEntry): string {
  switch (entry.type) {
    case "done":
      return aiColors.evidenceHigh;
    case "error":
      return statusColors.danger;
    case "classifying":
      return aiColors.classifying;
    case "labelled":
      return aiColors.evidenceHigh;
    case "params_extracting":
    case "params_extracted":
      return aiColors.understood;
    case "checking_cache":
    case "cache_hit":
    case "cache_miss":
      return aiColors.thinking;
  }
}

function formatElapsed(ms: number): string {
  if (ms < 1000) return `${Math.round(ms)}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
}

// ─── Styles ────────────────────────────────────────────────────────

const useStyles = makeStyles({
  root: {
    borderTop: `1px solid ${aiColors.annotationBorder}`,
    backgroundColor: aiColors.annotationBg,
    flexShrink: 0,
  },
  toggle: {
    display: "flex",
    alignItems: "center",
    columnGap: "5px",
    width: "100%",
    padding: "5px 14px",
    border: "none",
    backgroundColor: "transparent",
    cursor: "pointer",
    textAlign: "left",
    ":hover": {
      backgroundColor: `${aiColors.annotationBorder}44`,
    },
  },
  toggleLabel: {
    fontSize: "8.5px",
    fontFamily: fonts.mono,
    color: aiColors.thinking,
    letterSpacing: "0.5px",
    textTransform: "uppercase",
    fontWeight: 700,
  },
  toggleArrow: {
    fontSize: "8px",
    color: aiColors.thinking,
    marginLeft: "1px",
  },
  toggleSpacer: { flex: 1 },
  togglePhase: {
    fontSize: "8px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
  },
  entries: {
    maxHeight: "120px",
    overflow: "auto",
    paddingBottom: "4px",
    // Hide scrollbar visually while keeping it functional.
    scrollbarWidth: "none",
    "::-webkit-scrollbar": { display: "none" },
  },
  entry: {
    display: "flex",
    alignItems: "flex-start",
    columnGap: "7px",
    padding: "3px 14px 3px 12px",
    animationName: "itl-slide-in",
    animationDuration: "200ms",
    animationTimingFunction: "ease-out",
    animationFillMode: "both",
  },
  entryLine: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    flexShrink: 0,
    paddingTop: "3px",
  },
  entryDot: {
    width: "5px",
    height: "5px",
    borderRadius: "50%",
    flexShrink: 0,
  },
  entryConnector: {
    width: "1px",
    flex: 1,
    backgroundColor: aiColors.annotationBorder,
    marginTop: "2px",
    minHeight: "8px",
  },
  entryContent: {
    flex: 1,
    minWidth: 0,
    paddingBottom: "2px",
  },
  entryLabel: {
    fontSize: "9.5px",
    fontFamily: fonts.body,
    color: inkColors.ink2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  entryMeta: {
    display: "flex",
    alignItems: "center",
    columnGap: "5px",
    marginTop: "1px",
  },
  entryPosition: {
    fontSize: "8px",
    fontFamily: fonts.mono,
    color: inkColors.ink4,
    letterSpacing: "0.3px",
  },
  entryMetaTag: {
    fontSize: "8px",
    fontFamily: fonts.mono,
    color: inkColors.ink5,
    backgroundColor: surfaceColors.paperDim,
    padding: "0 4px",
    borderRadius: "3px",
    border: `1px solid ${surfaceColors.line}`,
  },
  entryElapsed: {
    fontSize: "7.5px",
    fontFamily: fonts.mono,
    color: inkColors.ink5,
    marginLeft: "auto",
    flexShrink: 0,
  },
});

// ─── Component ─────────────────────────────────────────────────────

export interface InventoryTimelineProps {
  entries: TimelineEntry[];
  phase: StreamPhase;
  /** True while the stream is active — controls auto-expand. */
  isLive: boolean;
}

function phaseLabel(phase: StreamPhase): string {
  switch (phase) {
    case "checking":
      return "checking";
    case "classifying":
      return "classifying";
    case "done":
      return "done";
    case "error":
      return "error";
    default:
      return "";
  }
}

const AUTO_EXPAND_THRESHOLD = 2;
const AUTO_COLLAPSE_DELAY_MS = 1500;

export const InventoryTimeline: React.FC<InventoryTimelineProps> = ({ entries, phase, isLive }) => {
  const styles = useStyles();
  const [expanded, setExpanded] = useState(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const prevEntriesLen = useRef(0);

  // Auto-expand once there's enough to show.
  useEffect(() => {
    if (isLive && phase === "classifying" && entries.length >= AUTO_EXPAND_THRESHOLD && !expanded) {
      setExpanded(true);
    }
  }, [isLive, phase, entries.length, expanded]);

  // Auto-collapse after done.
  useEffect(() => {
    if (phase === "done" && expanded) {
      const t = setTimeout(() => setExpanded(false), AUTO_COLLAPSE_DELAY_MS);
      return () => clearTimeout(t);
    }
  }, [phase, expanded]);

  // Scroll to bottom when a new entry arrives while expanded.
  useEffect(() => {
    if (expanded && entries.length > prevEntriesLen.current && bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
    prevEntriesLen.current = entries.length;
  }, [expanded, entries.length]);

  if (entries.length === 0) return null;

  // Show the last MAX_ENTRIES entries.
  const visible = entries.slice(-MAX_ENTRIES);

  return (
    <div className={styles.root}>
      <button
        type="button"
        className={styles.toggle}
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        data-testid="inventory-timeline-toggle"
      >
        <span className={styles.toggleArrow}>{expanded ? "▾" : "▸"}</span>
        <span className={styles.toggleLabel}>
          {entries.length} event{entries.length !== 1 ? "s" : ""}
        </span>
        <span className={styles.toggleSpacer} />
        {phaseLabel(phase) && <span className={styles.togglePhase}>{phaseLabel(phase)}</span>}
      </button>

      {expanded && (
        <div className={styles.entries} ref={bodyRef} data-testid="inventory-timeline-entries">
          {visible.map((entry, idx) => {
            const isLast = idx === visible.length - 1;
            const color = entryDotColor(entry);
            return (
              <div
                key={entry.id}
                className={styles.entry}
                // Stagger: each entry has a slight delay so they
                // cascade rather than all appearing at once on
                // initial expand.
                style={{ animationDelay: `${idx * 25}ms` }}
              >
                <div className={styles.entryLine}>
                  <span className={styles.entryDot} style={{ backgroundColor: color }} aria-hidden="true" />
                  {!isLast && <span className={styles.entryConnector} aria-hidden="true" />}
                </div>
                <div className={styles.entryContent}>
                  <div className={styles.entryLabel}>{entry.label}</div>
                  {(entry.position || entry.meta) && (
                    <div className={styles.entryMeta}>
                      {entry.position && <span className={styles.entryPosition}>{entry.position}</span>}
                      {entry.meta && <span className={styles.entryMetaTag}>{entry.meta}</span>}
                      <span className={styles.entryElapsed}>{formatElapsed(entry.elapsed)}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default InventoryTimeline;
